import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Check, 
  X as XIcon,
  Scale,
  Info,
  Trash2,
  Share2,
  Download,
  Copy,
  Loader2,
  CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { featureConfig } from '@/data/platforms';
import { useWizard } from '@/components/wizard/WizardContext';
import { ConfidenceIndicator, ConfidenceLegend, DataTypeLabel } from '@/components/shared/ConfidenceIndicators';
import { useSaveShare } from '@/hooks/useSaveShare';
import { exportComparisonToPDF } from '@/utils/pdfExport';
import { analyticsApi } from '@/services/api';
import { cn } from '@/lib/utils';
import { SEO } from '@/components/SEO';
import { SocialShareButtons } from '@/components/SocialShareButtons';

const FeatureCell = ({ available }) => (
  <div className="flex justify-center">
    {available ? (
      <div className="w-6 h-6 rounded-full bg-success/10 flex items-center justify-center">
        <Check className="w-4 h-4 text-success" />
      </div>
    ) : (
      <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
        <XIcon className="w-4 h-4 text-muted-foreground/50" />
      </div>
    )}
  </div>
);

const DataCell = ({ value }) => (
  <div className="flex justify-center">
    {value ? (
      <Badge variant="outline" className="border-success/30 bg-success-light text-success text-xs">
        Yes
      </Badge>
    ) : (
      <Badge variant="outline" className="text-xs">
        No
      </Badge>
    )}
  </div>
);

const CostIndicator = ({ level }) => {
  const symbols = ['Free', '£', '££', '£££'];
  return (
    <span className="font-medium text-primary">{symbols[level - 1] || '—'}</span>
  );
};

const SuitabilityBadge = ({ suitability }) => {
  const config = {
    suitable: { label: 'Suitable', className: 'bg-success text-success-foreground' },
    caution: { label: 'Caution', className: 'bg-warning text-warning-foreground' },
    'not-recommended': { label: 'Not recommended', className: 'bg-destructive text-destructive-foreground' },
  };
  const { label, className } = config[suitability] || config.caution;
  
  return (
    <Badge className={cn('text-xs', className)}>
      {label}
    </Badge>
  );
};

// Row label with confidence indicator
const RowLabel = ({ children, confidence = 'vendor', className }) => (
  <div className={cn('flex items-center gap-2 text-sm text-foreground', className)}>
    {children}
    <ConfidenceIndicator level={confidence} />
  </div>
);

export default function ComparisonPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toggleComparison, clearComparison, results, answers, navigateToPlatform, getPlatformById } = useWizard();
  const { isSaving, shareCode, saveResults, getShareUrl, copyShareLink, clearShare } = useSaveShare();
  const [copied, setCopied] = useState(false);
  
  const platformIds = searchParams.get('platforms')?.split(',').filter(Boolean) || [];
  const platforms = platformIds.map(id => getPlatformById(id)).filter(Boolean);
  const platformIdsKey = platformIds.join(',');

  // Track comparison view
  useEffect(() => {
    if (platforms.length >= 2) {
      // General analytics (for admin)
      analyticsApi.track('comparison_view', { 
        platform_ids: platformIds,
        platform_count: platforms.length 
      }).catch(() => {});
      
      // Vendor-specific analytics for each platform in comparison
      platforms.forEach(platform => {
        analyticsApi.trackVendor('comparison_inclusion', platform.id, 'comparison_page');
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformIdsKey]);
  
  if (platforms.length < 2) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto px-4">
            <Scale className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-foreground mb-2">Select platforms to compare</h1>
            <p className="text-muted-foreground mb-6">
              You need to select at least 2 platforms to compare. Go back to results and select platforms you&apos;d like to compare side by side.
            </p>
            <Button onClick={() => navigate('/')}>Back to results</Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Get suitability data from results if available
  const getPlatformResult = (id) => results.find(r => r.id === id);

  // Group features by category
  const featureGroups = {};
  Object.entries(featureConfig).forEach(([key, config]) => {
    if (!featureGroups[config.category]) {
      featureGroups[config.category] = [];
    }
    featureGroups[config.category].push({ key, label: config.label });
  });

  const handleRemove = (platformId) => {
    toggleComparison(platformId);
    const newIds = platformIds.filter(id => id !== platformId);
    if (newIds.length >= 2) {
      navigate(`/compare?platforms=${newIds.join(',')}`);
    } else {
      navigate('/');
    }
  };

  const handleSaveShare = async () => {
    const platformsWithResults = platforms.map(p => {
      const resultData = getPlatformResult(p.id);
      return {
        ...p,
        suitability: resultData?.suitability || 'caution',
        percentage: resultData?.percentage || 50,
        reasons: resultData?.reasons || { suitable: [], concerns: [] },
      };
    });
    await saveResults(answers, platformsWithResults, platformIds);
  };

  const handleCopyLink = async () => {
    if (shareCode) {
      const success = await copyShareLink(shareCode);
      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const handleExportPDF = () => {
    const platformsWithResults = platforms.map(p => {
      const resultData = getPlatformResult(p.id);
      return {
        ...p,
        suitability: resultData?.suitability || 'caution',
        percentage: resultData?.percentage || 50,
      };
    });
    exportComparisonToPDF(platformsWithResults, answers);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO pageType="comparison" />
      <Header />
      
      <main className="flex-1 w-full">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
          {/* Header - Mobile optimized */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="-ml-2 gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            
            {/* Action buttons - scrollable on mobile */}
            <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0 -mx-4 px-4 sm:mx-0 sm:px-0">
              {/* Social Share Dropdown */}
              <SocialShareButtons 
                url={window.location.href}
                title={`Comparing ${platforms.map(p => p.name).join(' vs ')} - GoCompareAI`}
                description="Check out this AI platform comparison on GoCompareAI"
                variant="dropdown"
              />

              {/* Export PDF */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportPDF}
                className="gap-1.5 whitespace-nowrap flex-shrink-0"
                data-testid="export-pdf-btn"
              >
                <Download className="w-4 h-4" />
                <span className="hidden xs:inline">Export PDF</span>
                <span className="xs:hidden">PDF</span>
              </Button>

              {/* Save for later sharing */}
              {!shareCode ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSaveShare}
                  disabled={isSaving}
                  className="gap-1.5 whitespace-nowrap flex-shrink-0"
                  data-testid="save-share-btn"
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Share2 className="w-4 h-4" />
                  )}
                  <span className="hidden xs:inline">{isSaving ? 'Saving...' : 'Save Link'}</span>
                  <span className="xs:hidden">{isSaving ? '...' : 'Save'}</span>
                </Button>
              ) : (
                <div className="flex gap-1 flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyLink}
                    className="gap-1.5 whitespace-nowrap"
                    data-testid="copy-link-btn"
                  >
                    {copied ? (
                      <CheckCircle className="w-4 h-4 text-success" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearShare}
                    className="px-2"
                  >
                    <XIcon className="w-4 h-4" />
                  </Button>
                </div>
              )}
              
              {/* Clear */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  clearComparison();
                  navigate('/');
                }}
                className="gap-1.5 whitespace-nowrap flex-shrink-0"
              >
                <Trash2 className="w-4 h-4" />
                <span className="hidden xs:inline">Clear</span>
              </Button>
            </div>
          </div>

          <div className="animate-fade-in">
            {/* Share Success Message */}
            {shareCode && (
              <Card className="mb-6 bg-success/5 border-success/20">
                <CardContent className="py-3 sm:py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-success flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-foreground">Comparison saved!</p>
                        <p className="text-xs text-muted-foreground">Share this link with others</p>
                      </div>
                    </div>
                    <code className="text-xs bg-muted px-2 py-1 rounded truncate max-w-full">
                      {getShareUrl(shareCode)}
                    </code>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="mb-4 sm:mb-6">
              <h1 className="text-2xl sm:text-3xl font-semibold text-foreground mb-1 sm:mb-2">
                Platform comparison
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground">
                Comparing {platforms.length} platforms based on features, pricing, and data handling.
              </p>
            </div>

            {/* Confidence Legend */}
            <Card className="mb-4 sm:mb-6 bg-muted/30 border-border/50">
              <CardContent className="py-2 sm:py-3">
                <ConfidenceLegend />
              </CardContent>
            </Card>

            {/* Comparison Content */}
            {/* Mobile view: Stacked cards */}
            <div className="block sm:hidden space-y-4 mb-6">
              {platforms.map(platform => {
                const resultData = getPlatformResult(platform.id);
                const isFictional = platform.tags?.includes('fictional');
                
                return (
                  <Card key={platform.id} className="relative">
                    <button
                      onClick={() => handleRemove(platform.id)}
                      className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-muted transition-colors"
                      title="Remove from comparison"
                    >
                      <XIcon className="w-4 h-4 text-muted-foreground" />
                    </button>
                    
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <h3 className="font-semibold text-foreground">
                            {platform.name}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {platform.provider}
                          </p>
                        </div>
                        
                        {resultData && (
                          <SuitabilityBadge suitability={resultData.suitability} />
                        )}
                      </div>
                      
                      {isFictional && (
                        <Badge variant="outline" className="text-xs mt-2">Example</Badge>
                      )}
                      
                      <p className="text-sm text-muted-foreground mt-3 line-clamp-2">
                        {platform.summary}
                      </p>
                      
                      <div className="flex gap-2 mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigateToPlatform(platform.id)}
                          className="text-xs flex-1"
                        >
                          View details
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              
              {/* Mobile: Simple comparison summary */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Quick comparison</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {['Cost level', 'Free tier', 'API Access'].map(feature => (
                    <div key={feature} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                      <span className="text-sm text-muted-foreground">{feature}</span>
                      <div className="flex gap-4">
                        {platforms.map(p => (
                          <div key={p.id} className="text-center min-w-[60px]">
                            <span className="text-xs font-medium text-foreground">
                              {feature === 'Cost level' && <CostIndicator level={p.costBand} />}
                              {feature === 'Free tier' && (
                                <FeatureCell available={p.pricing?.hasFreeeTier || p.pricing?.hasFreeTier} />
                              )}
                              {feature === 'API Access' && (
                                <FeatureCell available={p.features?.apiAccess} />
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
              
              <p className="text-xs text-muted-foreground text-center">
                View on a larger screen for detailed side-by-side comparison
              </p>
            </div>

            {/* Desktop view: Full comparison table */}
            <div className="hidden sm:block overflow-x-auto">
              <div className="min-w-[600px]">
                {/* Platform Headers */}
                <div className="grid gap-4 mb-6" style={{ gridTemplateColumns: `200px repeat(${platforms.length}, 1fr)` }}>
                  <div /> {/* Empty corner cell */}
                  {platforms.map(platform => {
                    const resultData = getPlatformResult(platform.id);
                    const isFictional = platform.tags?.includes('fictional');
                    
                    return (
                      <Card key={platform.id} className="relative">
                        <button
                          onClick={() => handleRemove(platform.id)}
                          className="absolute top-2 right-2 p-1 rounded-full hover:bg-muted transition-colors"
                          title="Remove from comparison"
                        >
                          <XIcon className="w-4 h-4 text-muted-foreground" />
                        </button>
                        
                        <CardContent className="pt-6 pb-4 text-center">
                          <h3 className="font-semibold text-lg text-foreground mb-1">
                            {platform.name}
                          </h3>
                          <p className="text-sm text-muted-foreground mb-2">
                            {platform.provider}
                          </p>
                          
                          {isFictional && (
                            <Badge variant="outline" className="text-xs mb-2">Example</Badge>
                          )}
                          
                          {resultData && (
                            <div className="mt-2">
                              <SuitabilityBadge suitability={resultData.suitability} />
                            </div>
                          )}
                          
                          <div className="mt-3 space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigateToPlatform(platform.id)}
                              className="text-xs"
                            >
                              View details
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Quick Info Section - FACTUAL */}
                <Card className="mb-6">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Overview</CardTitle>
                      <DataTypeLabel type="fact" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Summary */}
                      <div className="grid gap-4" style={{ gridTemplateColumns: `200px repeat(${platforms.length}, 1fr)` }}>
                        <RowLabel confidence="vendor">Summary</RowLabel>
                        {platforms.map(platform => (
                          <div key={platform.id} className="text-sm text-foreground py-2">
                            {platform.summary}
                          </div>
                        ))}
                      </div>
                      
                      {/* Cost */}
                      <div className="grid gap-4 border-t pt-4" style={{ gridTemplateColumns: `200px repeat(${platforms.length}, 1fr)` }}>
                        <RowLabel confidence="vendor">Cost level</RowLabel>
                        {platforms.map(platform => (
                          <div key={platform.id} className="py-2 text-center">
                            <CostIndicator level={platform.costBand} />
                          </div>
                        ))}
                      </div>
                      
                      {/* Free tier */}
                      <div className="grid gap-4 border-t pt-4" style={{ gridTemplateColumns: `200px repeat(${platforms.length}, 1fr)` }}>
                        <RowLabel confidence="vendor">Free tier</RowLabel>
                        {platforms.map(platform => (
                          <div key={platform.id} className="py-2">
                            <DataCell value={platform.pricing?.hasFreeeTier || platform.pricing?.hasFreeTier} />
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Features Section - FACTUAL */}
                <Card className="mb-6">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Features</CardTitle>
                      <DataTypeLabel type="fact" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    {Object.entries(featureGroups).map(([category, features]) => (
                      <div key={category} className="mb-4 last:mb-0">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                          {category}
                        </p>
                        {features.map(({ key, label }) => (
                          <div 
                            key={key}
                            className="grid gap-4 border-t py-3" 
                            style={{ gridTemplateColumns: `200px repeat(${platforms.length}, 1fr)` }}
                          >
                            <RowLabel confidence="vendor">{label}</RowLabel>
                            {platforms.map(platform => (
                              <FeatureCell key={platform.id} available={platform.features?.[key]} />
                            ))}
                          </div>
                        ))}
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Data & Security Section - FACTUAL */}
                <Card className="mb-6">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Data & Security</CardTitle>
                      <DataTypeLabel type="fact" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-0">
                      {/* Data location */}
                      <div className="grid gap-4 border-t py-3" style={{ gridTemplateColumns: `200px repeat(${platforms.length}, 1fr)` }}>
                        <RowLabel confidence="vendor">Data location</RowLabel>
                        {platforms.map(platform => (
                          <div key={platform.id} className="text-sm text-center text-muted-foreground">
                            {platform.dataHandling?.dataLocation || 'Not specified'}
                          </div>
                        ))}
                      </div>
                      
                      {/* Opt-out */}
                      <div className="grid gap-4 border-t py-3" style={{ gridTemplateColumns: `200px repeat(${platforms.length}, 1fr)` }}>
                        <RowLabel confidence="vendor">Opt-out available</RowLabel>
                        {platforms.map(platform => (
                          <DataCell key={platform.id} value={platform.dataHandling?.optOutAvailable} />
                        ))}
                      </div>
                      
                      {/* GDPR */}
                      <div className="grid gap-4 border-t py-3" style={{ gridTemplateColumns: `200px repeat(${platforms.length}, 1fr)` }}>
                        <RowLabel confidence="vendor">GDPR compliant</RowLabel>
                        {platforms.map(platform => (
                          <DataCell key={platform.id} value={platform.dataHandling?.gdprCompliant} />
                        ))}
                      </div>
                      
                      {/* HIPAA */}
                      <div className="grid gap-4 border-t py-3" style={{ gridTemplateColumns: `200px repeat(${platforms.length}, 1fr)` }}>
                        <RowLabel confidence="vendor">HIPAA compliant</RowLabel>
                        {platforms.map(platform => (
                          <DataCell key={platform.id} value={platform.dataHandling?.hipaaCompliant} />
                        ))}
                      </div>
                      
                      {/* SOC 2 */}
                      <div className="grid gap-4 border-t py-3" style={{ gridTemplateColumns: `200px repeat(${platforms.length}, 1fr)` }}>
                        <RowLabel confidence="vendor">SOC 2 certified</RowLabel>
                        {platforms.map(platform => (
                          <DataCell key={platform.id} value={platform.dataHandling?.soc2Certified} />
                        ))}
                      </div>
                      
                      {/* Enterprise protection */}
                      <div className="grid gap-4 border-t py-3" style={{ gridTemplateColumns: `200px repeat(${platforms.length}, 1fr)` }}>
                        <RowLabel confidence="vendor">Enterprise data protection</RowLabel>
                        {platforms.map(platform => (
                          <DataCell key={platform.id} value={platform.dataHandling?.enterpriseDataProtection} />
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Pricing Section - FACTUAL */}
                <Card className="mb-6">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Pricing Plans</CardTitle>
                      <DataTypeLabel type="fact" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4" style={{ gridTemplateColumns: `200px repeat(${platforms.length}, 1fr)` }}>
                      <div /> {/* Empty cell */}
                      {platforms.map(platform => (
                        <div key={platform.id} className="space-y-2">
                          {Array.isArray(platform.pricing?.paidPlans) ? (
                            platform.pricing.paidPlans.map((plan, i) => (
                              <div key={i} className="p-3 bg-muted/50 rounded-lg text-sm">
                                <div className="flex justify-between items-center mb-1">
                                  <span className="font-medium text-foreground">{plan.name}</span>
                                  <span className="text-primary font-medium">{plan.price}</span>
                                </div>
                                <p className="text-xs text-muted-foreground">{plan.features}</p>
                              </div>
                            ))
                          ) : platform.pricing?.paidPlans ? (
                            <div className="p-3 bg-muted/50 rounded-lg text-sm">
                              <span className="text-primary font-medium">{platform.pricing.paidPlans}</span>
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground">Pricing not available</p>
                          )}
                          {platform.pricing?.apiPricing && (
                            <p className="text-xs text-muted-foreground px-1">
                              API: {platform.pricing.apiPricing}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Strengths & Limitations - INTERPRETATION */}
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <Card>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base text-success">Strengths</CardTitle>
                        <DataTypeLabel type="interpretation" />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${platforms.length}, 1fr)` }}>
                        {platforms.map(platform => (
                          <div key={platform.id} className="space-y-1">
                            <p className="text-xs font-medium text-foreground mb-2">{platform.name}</p>
                            <ul className="space-y-1">
                              {(platform.pros || []).slice(0, 3).map((pro, i) => (
                                <li key={i} className="text-xs text-muted-foreground flex items-start gap-1">
                                  <Check className="w-3 h-3 text-success mt-0.5 flex-shrink-0" />
                                  {pro}
                                </li>
                              ))}
                              {(!platform.pros || platform.pros.length === 0) && (
                                <li className="text-xs text-muted-foreground">Not available</li>
                              )}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base text-amber-600">Limitations</CardTitle>
                        <DataTypeLabel type="interpretation" />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${platforms.length}, 1fr)` }}>
                        {platforms.map(platform => (
                          <div key={platform.id} className="space-y-1">
                            <p className="text-xs font-medium text-foreground mb-2">{platform.name}</p>
                            <ul className="space-y-1">
                              {(platform.cons || []).slice(0, 3).map((con, i) => (
                                <li key={i} className="text-xs text-muted-foreground flex items-start gap-1">
                                  <XIcon className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" />
                                  {con}
                                </li>
                              ))}
                              {(!platform.cons || platform.cons.length === 0) && (
                                <li className="text-xs text-muted-foreground">Not available</li>
                              )}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Disclaimer */}
                <Card className="bg-muted/50 border-border/50">
                  <CardContent className="flex items-start gap-3 py-4">
                    <Info className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
                    <div className="text-sm text-muted-foreground">
                      <p className="font-medium text-foreground mb-1">About this comparison</p>
                      <p>
                        <strong>Factual information</strong> is sourced from vendor documentation and official sources. 
                        <strong> Our assessment</strong> sections reflect independent analysis and research — these are interpretive 
                        judgments, not vendor claims. Some platforms shown are fictional examples for demonstration. 
                        Always verify current details directly with providers.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
            {/* End Desktop view */}
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
